import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Loader2, ScanSearch, Upload, FileDown, FileSpreadsheet, Trash2, AlertTriangle,
  HelpCircle, Info, ArrowLeft, Lock, Scale, Save, CheckCircle2, ArrowUpCircle, ArrowDownCircle, MinusCircle,
} from "lucide-react";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const grab = (url, filename) => {
  const token = localStorage.getItem("matiqs_token");
  fetch(`${API}${url}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
    .then((b) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch(() => toast.error("Could not build that export"));
};

const SEV = {
  critical: { label: "Critical", icon: AlertTriangle, cls: "bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200 border-red-300/60" },
  clarify: { label: "To clarify", icon: HelpCircle, cls: "bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 border-amber-300/60" },
  minor: { label: "Minor", icon: Info, cls: "bg-secondary text-muted-foreground border-border" },
};
const KIND = { missing_in_a: "Missing in A", missing_in_b: "Missing in B", conflict: "Conflict" };

const QFLAG = {
  match: { label: "Match", icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200 border-emerald-300/60" },
  over: { label: "Over-measured", icon: ArrowUpCircle, cls: "bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200 border-blue-300/60" },
  under: { label: "Under-measured", icon: ArrowDownCircle, cls: "bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 border-amber-300/60" },
  not_measured: { label: "Not measured", icon: MinusCircle, cls: "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-200 border-red-300/60" },
  missing_in_rfq: { label: "Not in BOQ", icon: AlertTriangle, cls: "bg-secondary text-muted-foreground border-border" },
};
const fmtNum = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));

const RFLAG = {
  increased: { label: "Increased", icon: ArrowUpCircle, cls: "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-200 border-red-300/60" },
  decreased: { label: "Decreased", icon: ArrowDownCircle, cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200 border-emerald-300/60" },
  unchanged: { label: "Unchanged", icon: MinusCircle, cls: "bg-secondary text-muted-foreground border-border" },
  new: { label: "New line", icon: AlertTriangle, cls: "bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200 border-blue-300/60" },
  removed: { label: "Removed", icon: AlertTriangle, cls: "bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 border-amber-300/60" },
};

const GapCard = ({ g, i }) => {
  const s = SEV[g.severity] || SEV.minor;
  return (
    <div data-testid={`gap-${i}`} className="bg-card border border-border rounded-2xl p-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-bold uppercase tracking-wide ${s.cls}`}>
          <s.icon className="w-3 h-3" /> {s.label}
        </span>
        <span className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[11px] font-bold uppercase tracking-wide">
          {KIND[g.kind] || g.kind}
        </span>
        {(g.reference_a || g.reference_b) && (
          <span className="text-[11px] font-mono text-muted-foreground">
            {[g.reference_a && `A: ${g.reference_a}`, g.reference_b && `B: ${g.reference_b}`].filter(Boolean).join("  ·  ")}
          </span>
        )}
      </div>
      <div className="font-semibold text-slate-900 dark:text-slate-100 mt-3">{g.title}</div>
      <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{g.detail}</p>
      {g.clarification && (
        <p className="text-sm mt-3 pl-3 border-l-2 border-brand/50 italic text-slate-700 dark:text-slate-300">
          Ask: {g.clarification}
        </p>
      )}
    </div>
  );
};

export default function QReview() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", review_type: "scope" });
  const [files, setFiles] = useState({ a: null, b: null });
  const [qMode, setQMode] = useState("rfq");      // quantity source: 'rfq' | 'files'
  const [quotes, setQuotes] = useState([]);
  const [selRfq, setSelRfq] = useState("");
  const [qEdits, setQEdits] = useState({});       // index -> edited measurement qty
  const aRef = useRef();
  const bRef = useRef();

  const load = () => api.get("/qreview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/rfqs").then((r) => setQuotes(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => {});
  }, []);
  useEffect(() => {
    if (params.get("type") === "quantity") {
      setForm((f) => ({ ...f, review_type: "quantity" }));
      const rid = params.get("rfq");
      if (rid) { setQMode("rfq"); setSelRfq(rid); }
    }
  }, [params]);

  const runQuantity = async () => {
    if (qMode === "rfq" && !selRfq) return toast.error("Pick the quote whose BOQ you want to check");
    if (qMode === "files" && !files.a) return toast.error("Upload the RFQ BOQ");
    if (!files.b) return toast.error("Upload your measurement BOQ");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file_b", files.b);
      if (qMode === "files") fd.append("file_a", files.a);
      const qs = new URLSearchParams({ name: form.name, rfq_id: qMode === "rfq" ? selRfq : "" });
      const { data } = await api.post(`/qreview/quantity?${qs}`, fd, { timeout: 300000 });
      toast.success("Quantity review ready");
      setOpen(data.review);
      setQEdits({});
      setFiles({ a: null, b: null });
      setForm({ name: "", review_type: "quantity" });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "The quantity review could not be completed", { duration: 9000 });
    } finally {
      setBusy(false);
    }
  };

  const saveQtyEdits = async () => {
    const rows = Object.entries(qEdits).map(([index, v]) => ({ index: Number(index), measurement_qty: v === "" ? null : Number(v) }));
    if (!rows.length) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/qreview/${open.id}/quantity`, { rows });
      setOpen(data.review);
      setQEdits({});
      toast.success("Quantities updated");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const runRate = async () => {
    if (!files.a || !files.b) return toast.error("Upload both the current and the old quote");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file_a", files.a);
      fd.append("file_b", files.b);
      const { data } = await api.post(`/qreview/rate?name=${encodeURIComponent(form.name)}`, fd, { timeout: 300000 });
      toast.success("Rate variance ready");
      setOpen(data.review);
      setFiles({ a: null, b: null });
      setForm({ name: "", review_type: "rate" });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "The rate review could not be completed", { duration: 9000 });
    } finally {
      setBusy(false);
    }
  };


  const pushToQuote = async () => {
    if (Object.keys(qEdits).length) return toast.error("Save your changes first, then push to the quote");
    setBusy(true);
    try {
      const { data } = await api.post(`/qreview/${open.id}/push-to-quote`);
      toast.success(`${data.updated} of ${data.total} quantities pushed to the quote — price it next`, {
        duration: 8000,
        action: data.rfq_id ? { label: "Open quote", onClick: () => navigate(`/rfq/${data.rfq_id}`) } : undefined,
      });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not push to the quote");
    } finally {
      setBusy(false);
    }
  };

  const run = async (e) => {
    e.preventDefault();
    if (form.review_type === "quantity") return runQuantity();
    if (form.review_type === "rate") return runRate();
    if (!files.a || !files.b) return toast.error("Add both documents first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file_a", files.a);
      fd.append("file_b", files.b);
      const params = new URLSearchParams({ name: form.name, review_type: form.review_type });
      const { data } = await api.post(`/qreview?${params}`, fd, { timeout: 300000 });
      toast.success(`${data.review.gaps.length} gap${data.review.gaps.length === 1 ? "" : "s"} found · coverage ${data.review.coverage_score}%`);
      setOpen(data.review);
      setFiles({ a: null, b: null });
      setForm({ name: "", review_type: form.review_type });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "The review could not be completed", { duration: 9000 });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await api.delete(`/qreview/${id}`).catch(() => {});
    if (open?.id === id) setOpen(null);
    load();
  };

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const u = data.usage;
  const blocked = !u.unlimited && u.remaining <= 0;

  if (open && open.review_type === "rate") {
    const rows = open.rows || [];
    const c = open.counts || {};
    return (
      <div data-testid="qreview-rate-report" className="space-y-6">
        <button onClick={() => setOpen(null)} className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to QReview
        </button>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-brand font-bold">Rate variance — current vs old quote</div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-2">{open.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">Current: {open.doc_a?.filename} · Old: {open.doc_b?.filename}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="rate-export-xlsx" onClick={() => grab(`/qreview/${open.id}/xlsx`, `RateVariance-${open.name}.xlsx`)} className="h-10 gap-2 font-semibold">
              <FileSpreadsheet className="w-4 h-4" /> Excel
            </Button>
            <Button variant="outline" data-testid="rate-export-pdf" onClick={() => grab(`/qreview/${open.id}/pdf`, `RateVariance-${open.name}.pdf`)} className="h-10 gap-2 font-semibold">
              <FileDown className="w-4 h-4" /> PDF
            </Button>
          </div>
        </div>
        <div className="bg-card border border-border rounded-2xl p-6">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold">Summary</div>
          <p className="text-sm mt-1.5 leading-relaxed">{open.summary}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {["increased", "decreased", "unchanged", "new", "removed"].map((k) => (
              <span key={k} className={`px-2 py-0.5 rounded-md border text-[11px] font-bold uppercase tracking-wide ${RFLAG[k].cls}`}>
                {c[k] || 0} {RFLAG[k].label}
              </span>
            ))}
          </div>
        </div>
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rate-table">
              <thead className="bg-secondary text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left font-bold px-3 py-3 min-w-[240px]">Description</th>
                  <th className="text-left font-bold px-3 py-3">Unit</th>
                  <th className="text-right font-bold px-3 py-3">Old rate</th>
                  <th className="text-right font-bold px-3 py-3">Current rate</th>
                  <th className="text-right font-bold px-3 py-3">Diff</th>
                  <th className="text-right font-bold px-3 py-3">Var %</th>
                  <th className="text-left font-bold px-3 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r, i) => {
                  const meta = RFLAG[r.flag] || RFLAG.unchanged;
                  return (
                    <tr key={i} data-testid={`rate-row-${i}`}>
                      <td className="px-3 py-2.5">{r.description}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{r.unit || "—"}</td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmtNum(r.old_rate)}</td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmtNum(r.current_rate)}</td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmtNum(r.diff)}</td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums">{r.variance_pct === null || r.variance_pct === undefined ? "—" : `${r.variance_pct}%`}</td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-bold uppercase tracking-wide ${meta.cls}`}>
                          <meta.icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {rows.length === 0 && <div className="px-6 py-10 text-center text-sm text-muted-foreground">No line items to compare.</div>}
        </div>
      </div>
    );
  }

  if (open && open.review_type === "quantity") {
    const rows = open.rows || [];
    const extra = open.measurement_only || [];
    const c = open.counts || {};
    return (
      <div data-testid="qreview-quantity-report" className="space-y-6">
        <button onClick={() => { setOpen(null); setQEdits({}); }} className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to QReview
        </button>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-brand font-bold">Quantity review</div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-2">{open.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              RFQ BOQ: {open.doc_a?.filename} · Measurement: {open.doc_b?.filename}
            </p>
          </div>
          <div className="flex gap-2">
            {open.source_rfq_id && (
              <Button data-testid="qty-push-button" onClick={pushToQuote} disabled={busy}
                      className="h-10 gap-2 font-semibold">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpCircle className="w-4 h-4" />} Push quantities to quote
              </Button>
            )}
            {Object.keys(qEdits).length > 0 && (
              <Button data-testid="qty-save-button" onClick={saveQtyEdits} disabled={busy} className="h-10 gap-2 font-semibold">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
              </Button>
            )}
            <Button variant="outline" data-testid="qty-export-xlsx" onClick={() => grab(`/qreview/${open.id}/xlsx`, `QtyReview-${open.name}.xlsx`)} className="h-10 gap-2 font-semibold">
              <FileSpreadsheet className="w-4 h-4" /> Excel
            </Button>
            <Button variant="outline" data-testid="qty-export-pdf" onClick={() => grab(`/qreview/${open.id}/pdf`, `QtyReview-${open.name}.pdf`)} className="h-10 gap-2 font-semibold">
              <FileDown className="w-4 h-4" /> PDF
            </Button>
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold">Summary</div>
          <p className="text-sm mt-1.5 leading-relaxed">{open.summary}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {["match", "over", "under", "not_measured", "extra"].map((k) => {
              const meta = QFLAG[k === "extra" ? "missing_in_rfq" : k];
              return (
                <span key={k} className={`px-2 py-0.5 rounded-md border text-[11px] font-bold uppercase tracking-wide ${meta.cls}`}>
                  {c[k] || 0} {k === "extra" ? "Extra" : meta.label}
                </span>
              );
            })}
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="qty-table">
              <thead className="bg-secondary text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left font-bold px-3 py-3">Heading / sub-heading</th>
                  <th className="text-left font-bold px-3 py-3 min-w-[220px]">Description</th>
                  <th className="text-left font-bold px-3 py-3">Unit</th>
                  <th className="text-right font-bold px-3 py-3">RFQ qty</th>
                  <th className="text-right font-bold px-3 py-3">Measurement</th>
                  <th className="text-right font-bold px-3 py-3">Diff</th>
                  <th className="text-right font-bold px-3 py-3">Var %</th>
                  <th className="text-left font-bold px-3 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => {
                  const meta = QFLAG[r.flag] || QFLAG.match;
                  const edited = qEdits[r.index] !== undefined;
                  const val = edited ? qEdits[r.index] : (r.measurement_qty ?? "");
                  return (
                    <tr key={r.index} data-testid={`qty-row-${r.index}`} className="align-top">
                      <td className="px-3 py-2.5 text-xs text-muted-foreground max-w-[220px]">
                        {r.heading || "—"}
                        {r.qualification && (
                          <span data-testid={`qty-note-${r.index}`} className="block mt-1 text-[11px] italic text-brand">ⓘ {r.qualification}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">{r.description}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{r.unit || "—"}</td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmtNum(r.rfq_qty)}</td>
                      <td className="px-3 py-2.5 text-right">
                        <Input data-testid={`qty-meas-${r.index}`} value={val}
                               onChange={(e) => setQEdits((q) => ({ ...q, [r.index]: e.target.value }))}
                               className="h-9 w-24 text-right text-sm ml-auto font-mono" placeholder="—" />
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmtNum(r.diff)}</td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums">{r.variance_pct === null || r.variance_pct === undefined ? "—" : `${r.variance_pct}%`}</td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-bold uppercase tracking-wide ${meta.cls}`}>
                          <meta.icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {extra.map((x, i) => (
                  <tr key={`x-${i}`} data-testid={`qty-extra-${i}`} className="align-top bg-secondary/30">
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">—</td>
                    <td className="px-3 py-2.5">{x.description}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{x.unit || "—"}</td>
                    <td className="px-3 py-2.5 text-right text-muted-foreground">—</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmtNum(x.qty)}</td>
                    <td className="px-3 py-2.5 text-right text-muted-foreground">—</td>
                    <td className="px-3 py-2.5 text-right text-muted-foreground">—</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-bold uppercase tracking-wide ${QFLAG.missing_in_rfq.cls}`}>
                        <AlertTriangle className="w-3 h-3" /> Not in BOQ
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length === 0 && <div className="px-6 py-10 text-center text-sm text-muted-foreground">No BOQ lines to compare.</div>}
        </div>
      </div>
    );
  }

  if (open) {
    return (
      <div data-testid="qreview-report" className="space-y-6">
        <button onClick={() => setOpen(null)} className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to QReview
        </button>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-brand font-bold">Gap analysis</div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-2">{open.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              A: {open.doc_a?.filename} · B: {open.doc_b?.filename} · quantities excluded by design
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="qreview-export-xlsx" onClick={() => grab(`/qreview/${open.id}/xlsx`, `QReview-${open.name}.xlsx`)} className="h-10 gap-2 font-semibold">
              <FileSpreadsheet className="w-4 h-4" /> Excel
            </Button>
            <Button variant="outline" data-testid="qreview-export-pdf" onClick={() => grab(`/qreview/${open.id}/pdf`, `QReview-${open.name}.pdf`)} className="h-10 gap-2 font-semibold">
              <FileDown className="w-4 h-4" /> PDF
            </Button>
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 grid sm:grid-cols-4 gap-5">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold">Coverage</div>
            <div data-testid="coverage-score" className="font-display text-3xl font-extrabold mt-1">{open.coverage_score}%</div>
          </div>
          <div className="sm:col-span-3">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold">Verdict</div>
            <p className="text-sm mt-1.5 leading-relaxed">{open.summary}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {Object.entries(SEV).map(([k, s]) => (
                <span key={k} className={`px-2 py-0.5 rounded-md border text-[11px] font-bold uppercase tracking-wide ${s.cls}`}>
                  {open.counts?.[k] || 0} {s.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {open.gaps.map((g, i) => <GapCard key={g.id} g={g} i={i} />)}
          {open.gaps.length === 0 && (
            <div className="bg-card border border-border rounded-2xl p-10 text-center text-sm text-muted-foreground">
              No scope gaps were found between these two documents.
            </div>
          )}
        </div>

        {open.clarifications?.length > 0 && (
          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="font-semibold text-slate-900 dark:text-slate-100">Clarifications to send</div>
            <ul className="mt-3 space-y-2">
              {open.clarifications.map((c, i) => (
                <li key={i} className="text-sm text-muted-foreground">• {c}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="qreview-page" className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-brand font-bold">QReview</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-2">
          Document review with gap analysis
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Put two documents side by side — BOQ against specification, contract, drawing or tender
          addendum — and see what is missing before it costs you. Or run a Quantity Review to compare
          BOQ quantities against your own measurement take-off.
        </p>
      </div>

      <form onSubmit={run} className="bg-card border border-border rounded-2xl p-6 space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="qr-name">Review name</Label>
            <Input id="qr-name" data-testid="qreview-name-input" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Tower B glazing — BOQ vs specification" className="mt-1.5" />
          </div>
          <div>
            <Label>Review type</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {data.review_types.map((t) => (
                <button key={t.id} type="button" data-testid={`qreview-type-${t.id}`}
                        onClick={() => setForm({ ...form, review_type: t.id })}
                        className={`px-3 h-9 rounded-lg text-xs font-bold transition-colors border ${
                          form.review_type === t.id ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  {t.id === "scope" ? "Scope review" : t.id === "contract" ? "Contract vs contract" : "Document A vs B"}
                </button>
              ))}
              <button type="button" data-testid="qreview-type-quantity"
                      onClick={() => setForm({ ...form, review_type: "quantity" })}
                      className={`px-3 h-9 rounded-lg text-xs font-bold transition-colors border inline-flex items-center gap-1.5 ${
                        form.review_type === "quantity" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                <Scale className="w-3.5 h-3.5" /> Quantity review
              </button>
              <button type="button" data-testid="qreview-type-rate"
                      onClick={() => setForm({ ...form, review_type: "rate" })}
                      className={`px-3 h-9 rounded-lg text-xs font-bold transition-colors border inline-flex items-center gap-1.5 ${
                        form.review_type === "rate" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                <ArrowUpCircle className="w-3.5 h-3.5" /> Current vs old quote
              </button>
            </div>
          </div>
        </div>

        {form.review_type === "quantity" ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {[["rfq", "From one of my quotes"], ["files", "Upload both BOQs"]].map(([k, lbl]) => (
                <button key={k} type="button" data-testid={`qty-mode-${k}`}
                        onClick={() => setQMode(k)}
                        className={`px-3 h-9 rounded-lg text-xs font-bold transition-colors border ${
                          qMode === k ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-muted-foreground bg-secondary/50 border border-border rounded-lg px-3 py-2 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>We auto-match each contractor BOQ line to your measurement BOQ. Where two lines share a description we use their heading/sub-heading to tell them apart and note which one we used. You can adjust any measured quantity afterwards.</span>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {qMode === "rfq" ? (
                <div>
                  <Label>Contractor BOQ — pick a quote</Label>
                  <select data-testid="qty-rfq-select" value={selRfq}
                          onChange={(e) => setSelRfq(e.target.value)}
                          className="mt-1.5 flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <option value="">Select a quote…</option>
                    {quotes.map((qq) => (
                      <option key={qq.id} value={qq.id}>{qq.name || qq.quote_ref || qq.id}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <Label>RFQ BOQ (contractor's bill of quantities)</Label>
                  <input ref={aRef} type="file" className="hidden" data-testid="qty-file-a"
                         accept=".pdf,.xlsx,.xls,.csv,.docx,.txt"
                         onChange={(e) => setFiles((f) => ({ ...f, a: e.target.files[0] }))} />
                  <button type="button" onClick={() => aRef.current?.click()} data-testid="qty-pick-a"
                          className="w-full mt-1.5 flex items-center gap-2 px-4 h-11 rounded-xl border border-dashed border-border hover:border-primary/50 text-sm transition-colors">
                    <Upload className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="truncate">{files.a?.name || "Excel, PDF or CSV"}</span>
                  </button>
                </div>
              )}
              <div>
                <Label>Measurement BOQ (your take-off)</Label>
                <input ref={bRef} type="file" className="hidden" data-testid="qty-file-b"
                       accept=".pdf,.xlsx,.xls,.csv,.docx,.txt"
                       onChange={(e) => setFiles((f) => ({ ...f, b: e.target.files[0] }))} />
                <button type="button" onClick={() => bRef.current?.click()} data-testid="qty-pick-b"
                        className="w-full mt-1.5 flex items-center gap-2 px-4 h-11 rounded-xl border border-dashed border-border hover:border-primary/50 text-sm transition-colors">
                  <Upload className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="truncate">{files.b?.name || "Excel, PDF or CSV"}</span>
                </button>
              </div>
            </div>
          </div>
        ) : form.review_type === "rate" ? (
          <div className="space-y-4">
            <div className="text-[11px] text-muted-foreground bg-secondary/50 border border-border rounded-lg px-3 py-2 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>Upload the current quote and a previous (old) quote for the same scope. We match the line items and show how each unit rate has moved — increased, decreased, unchanged, new or removed.</span>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Current quote</Label>
                <input ref={aRef} type="file" className="hidden" data-testid="rate-file-a"
                       accept=".pdf,.xlsx,.xls,.csv,.docx,.txt"
                       onChange={(e) => setFiles((f) => ({ ...f, a: e.target.files[0] }))} />
                <button type="button" onClick={() => aRef.current?.click()} data-testid="rate-pick-a"
                        className="w-full mt-1.5 flex items-center gap-2 px-4 h-11 rounded-xl border border-dashed border-border hover:border-primary/50 text-sm transition-colors">
                  <Upload className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="truncate">{files.a?.name || "Excel, PDF or CSV"}</span>
                </button>
              </div>
              <div>
                <Label>Old quote (to compare against)</Label>
                <input ref={bRef} type="file" className="hidden" data-testid="rate-file-b"
                       accept=".pdf,.xlsx,.xls,.csv,.docx,.txt"
                       onChange={(e) => setFiles((f) => ({ ...f, b: e.target.files[0] }))} />
                <button type="button" onClick={() => bRef.current?.click()} data-testid="rate-pick-b"
                        className="w-full mt-1.5 flex items-center gap-2 px-4 h-11 rounded-xl border border-dashed border-border hover:border-primary/50 text-sm transition-colors">
                  <Upload className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="truncate">{files.b?.name || "Excel, PDF or CSV"}</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {[["a", "Document A — usually your BOQ or quotation", aRef], ["b", "Document B — specification, contract, drawing or addendum", bRef]].map(([k, label, ref]) => (
            <div key={k}>
              <Label>{label}</Label>
              <input ref={ref} type="file" className="hidden" data-testid={`qreview-file-${k}`}
                     accept=".pdf,.xlsx,.xls,.csv,.docx,.txt"
                     onChange={(e) => setFiles((f) => ({ ...f, [k]: e.target.files[0] }))} />
              <button type="button" onClick={() => ref.current?.click()}
                      data-testid={`qreview-pick-${k}`}
                      className="w-full mt-1.5 flex items-center gap-2 px-4 h-11 rounded-xl border border-dashed border-border hover:border-primary/50 text-sm transition-colors">
                <Upload className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="truncate">{files[k]?.name || "PDF, Excel, Word or text file"}</span>
              </button>
            </div>
          ))}
        </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            {u.unlimited
              ? `${u.plan_name} plan · unlimited reviews`
              : `${u.plan_name} plan · ${u.remaining} of ${u.limit} reviews left this month`}
          </div>
          <Button type="submit" data-testid="run-qreview-button" disabled={busy || blocked}
                  className="h-11 gap-2 font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : blocked ? <Lock className="w-4 h-4" /> : form.review_type === "quantity" ? <Scale className="w-4 h-4" /> : form.review_type === "rate" ? <ArrowUpCircle className="w-4 h-4" /> : <ScanSearch className="w-4 h-4" />}
            {busy ? (form.review_type === "quantity" ? "Comparing quantities…" : form.review_type === "rate" ? "Comparing rates…" : "Reviewing documents…") : blocked ? "Monthly limit reached" : form.review_type === "quantity" ? "Run Quantity Review" : form.review_type === "rate" ? "Run Rate Variance" : "Run QReview"}
          </Button>
        </div>
        {blocked && (
          <div data-testid="qreview-limit-note" className="text-xs text-amber-800 dark:text-amber-200 bg-amber-50/70 dark:bg-amber-950/20 border border-amber-300/70 rounded-xl px-4 py-3">
            Your {u.plan_name} plan includes {u.limit} reviews a month. Upgrade for a higher allowance.
          </div>
        )}
      </form>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border font-semibold text-slate-900 dark:text-slate-100">
          Past reviews
        </div>
        {data.reviews.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">Nothing reviewed yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {data.reviews.map((rv, i) => (
              <div key={rv.id} data-testid={`review-row-${i}`} className="flex items-center justify-between gap-3 px-6 py-4">
                <button onClick={() => { setOpen(rv); setQEdits({}); }} data-testid={`open-review-${i}`} className="text-left min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate hover:text-primary transition-colors">
                    {rv.review_type === "quantity" && <Scale className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-brand" />}
                    {rv.review_type === "rate" && <ArrowUpCircle className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-brand" />}
                    {rv.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {rv.review_type === "quantity"
                      ? `quantity · ${(rv.counts?.match || 0)} match · ${(rv.counts?.over || 0) + (rv.counts?.under || 0)} variance · ${rv.counts?.not_measured || 0} not measured`
                      : rv.review_type === "rate"
                      ? `rate · ${(rv.counts?.increased || 0)} up · ${(rv.counts?.decreased || 0)} down · ${(rv.counts?.unchanged || 0)} same`
                      : `coverage ${rv.coverage_score}% · ${rv.gaps?.length || 0} gaps${rv.counts?.critical ? ` · ${rv.counts.critical} critical` : ""}`}
                  </div>
                </button>
                <button data-testid={`delete-review-${i}`} onClick={() => remove(rv.id)}
                        className="w-8 h-8 rounded-md text-muted-foreground hover:text-destructive flex items-center justify-center">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
